import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyS, BrandM, BrandS, BrandXXS } from 'atoms/typography';
import { Dialog } from 'components/dialog';
import { useIsMobile } from 'hooks/device/useIsMobile';
import { laptopAndAbove } from 'style/mediaQuery';

import type { TaskCampaignDialog_taskCampaignsHomepageTutorial } from './__generated__/index.graphql';
import bg from './assets/bg.webp';

const Root = styled(Vertical).attrs({ gap: 0 })`
  height: 100%;
`;
const CloseButtonWrapper = styled.div`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
`;
const Header = styled(Vertical).attrs({ center: true })`
  min-height: 150px;
  background: no-repeat center / cover url(${bg});

  @media ${laptopAndAbove} {
    height: 250px;
  }
`;
const SubHeader = styled(Vertical).attrs({ center: true, gap: 0.5 })`
  width: 100%;
  background: linear-gradient(to right, #fae504, #cd321e);
  padding: var(--unit) 0;
`;
const Body = styled(Vertical).attrs({ gap: 5 })`
  padding: var(--quadruple-unit) calc(6 * var(--unit));
  & > * {
    flex: 1;
  }
  @media ${laptopAndAbove} {
    flex-direction: row;
  }
`;
const Footer = styled(Horizontal).attrs({ gap: 2, center: true })`
  width: 100%;
  margin-top: auto;
  padding: 0 var(--double-unit) var(--double-unit) var(--double-unit);

  @media ${laptopAndAbove} {
    padding: 0 var(--double-unit) var(--quadruple-unit) var(--double-unit);
  }
`;

type Props = {
  open?: boolean;
  onClose: () => void;
  taskCampaignsHomepageTutorial: TaskCampaignDialog_taskCampaignsHomepageTutorial;
};
export const TaskCampaignDialog = ({
  open,
  onClose,
  taskCampaignsHomepageTutorial,
}: Props) => {
  const isMobile = useIsMobile();

  const { marketUrl, viewStreaksUrl, sections } = taskCampaignsHomepageTutorial;

  return (
    <Dialog open={open} onClose={onClose} hideHeader maxWidth="lg">
      {({ CloseButton }) => (
        <Root>
          <CloseButtonWrapper>
            <CloseButton onClose={onClose} />
          </CloseButtonWrapper>
          <Header />
          <SubHeader>
            <BrandXXS color="var(--c-nl-700)">
              <FormattedMessage
                id="TaskCampaignDialog.subheader.title"
                defaultMessage="Beat the game"
              />
            </BrandXXS>
            <BrandM color="var(--c-black)">
              <FormattedMessage
                id="TaskCampaignDialog.subheader.subtitle"
                defaultMessage="How it works"
              />
            </BrandM>
          </SubHeader>
          <Body>
            {sections.map(({ iconUrl, title, description }) => (
              <Vertical key={title}>
                <Vertical gap={1.5} center>
                  <img height={100} src={iconUrl} alt="" />
                  <BrandS className="text-center">{title}</BrandS>
                </Vertical>
                <BodyS color="var(--c-nd-600)" className="text-center">
                  {description}
                </BodyS>
              </Vertical>
            ))}
          </Body>
          <Footer>
            <Button
              color="secondary"
              size="medium"
              to={viewStreaksUrl}
              fullWidth={isMobile}
            >
              <FormattedMessage
                id="TaskCampaignDialog.footer.cta1"
                defaultMessage="View Streaks"
              />
            </Button>
            <Button
              color="primary"
              size="medium"
              to={marketUrl}
              fullWidth={isMobile}
            >
              <FormattedMessage
                id="TaskCampaignDialog.footer.cta2"
                defaultMessage="Shop the Market"
              />
            </Button>
          </Footer>
        </Root>
      )}
    </Dialog>
  );
};

TaskCampaignDialog.fragments = {
  taskCampaignsHomepageTutorial: gql`
    fragment TaskCampaignDialog_taskCampaignsHomepageTutorial on TaskCampaignsHomepageTutorial {
      marketUrl
      viewStreaksUrl
      sections {
        slug
        iconUrl
        title
        description
      }
    }
  ` as TypedDocumentNode<TaskCampaignDialog_taskCampaignsHomepageTutorial>,
};
